import React, { useState } from 'react';
import useClickOutside from '../hooks/useClickOutside';
import { Field } from 'formik';
import _ from 'lodash';
import { useFocus } from '../hooks/useFocus';
import { PremiumLabelBig } from './ui/PremiumLabelBig';
import { PremiumLabelSmall } from './ui/PremiumLabelSmall';
import { PremiumUnit } from './ui/PremiumUnit';
import { PremiumInputContainer } from './ui/PremiumInputContainer';
import { LabelAndValue } from './ui/LabelAndValue';
import { UnitsForLabelAndValue } from './ui/UnitsForLabelAndValue';
import { Value } from './ui/Value';
import { RequiredIndicator } from './ui/RequiredIndicator';
import { Invisible } from './ui/Invisible';
import { RadioButton } from './ui/RadioButton';
import { DropdownContainer } from './ui/DropdownContainer';
import { DropdownItem } from './ui/DropdownItem';
import moment from 'moment';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import Ball from '../ball/ball';
import SelectWithSearchInput from '../../modals/SelectWithSearchInput';
import { PhoneInput } from '@ontruck/fe-ui';
import ticketService from 'modules/emergencies/ticketService';
import { Input } from '@ontruck/fe-ui';

export const PremiumInput = ({ className, id, ...props }) => {
  const {
    label,
    type = 'text',
    units = null,
    value = null,
    min = 1,
    isDisabled = false,
    isRequired = false,
    hasError = false,
  } = props;
  const [hasFocus, setHasFocus] = useState(false);
  const clickRefFrom = React.useRef();
  useClickOutside(clickRefFrom, () => {
    setHasFocus(false);
  });
  const [inputRef, setInputFocus] = useFocus();

  const hasValue = value !== null && value !== '' && (value !== 0 || value >= min);

  const showInput = hasFocus || hasValue;
  const { field, form } = props;
  const { disableClear } = props;

  const cleanPropsForInput = ticketService.utils.clearPropsForInput(props);
  const cleanFormPropsForInput = ticketService.utils.clearPropsForInput(props.form);
  const cleanFieldPropsForInput = ticketService.utils.clearPropsForInput(props.field);

  //   RADIO
  if (type === 'radio') {
    return (
      <RadioButton
        checked={props.checked}
        isDisabled={isDisabled}
        onClick={() => {
          if (isDisabled) return;
          form.setFieldValue(field.name, field.value);
        }}
      >
        {props.children}
      </RadioButton>
    );
  }

  // SELECT
  if (type === 'select') {
    const availableOptions = props.availableOptions;

    const selectedOption = availableOptions.find((l) => l.value === value);

    const setValue = (value, field) => {
      if (props.onChange !== undefined) {
        // we send the value to the parent component
        props.onChange(value, form);
      } else {
        form.setFieldValue(field.name, value);
      }
    };

    return (
      <>
        <PremiumInputContainer
          width={props.width}
          isDisabled={isDisabled}
          hasError={hasError}
          className={className}
          hasFocus={hasFocus}
          ref={clickRefFrom}
          onClick={() => {
            if (isDisabled) return;
            setHasFocus(true);
          }}
        >
          {hasFocus && (
            <DropdownContainer>
              {availableOptions.map((item) => (
                <DropdownItem
                  key={item.value}
                  isSelected={item.value === value}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (isDisabled) return;
                    setValue(item.value, field);
                    setHasFocus(false);
                  }}
                >
                  {item.color && <Ball backgroundColor={item.color} />}
                  {item.label}
                </DropdownItem>
              ))}
            </DropdownContainer>
          )}
          <LabelAndValue>
            <PremiumLabelSmall>
              {label}
              {isRequired && <RequiredIndicator />}
            </PremiumLabelSmall>
            <Value>
              {selectedOption?.label}
              <Invisible>
                <input
                  onFocus={() => {
                    setHasFocus(true);
                  }}
                  onBlur={() => {
                    setHasFocus(false);
                  }}
                />
              </Invisible>
            </Value>
          </LabelAndValue>
          <UnitsForLabelAndValue noPadding={true}>
            <PremiumUnit>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10.9342 6.20291C10.8435 6.07757 10.6731 6.00002 10.4884 6H5.51147C5.32689 6.00007 5.15666 6.07757 5.06599 6.20282C4.97532 6.32807 4.97827 6.48165 5.0737 6.60473L7.56218 9.80778C7.65489 9.9271 7.82091 10 7.99996 10C8.179 10 8.34502 9.9271 8.43773 9.80778L10.9262 6.60473C11.0217 6.4817 11.0247 6.32816 10.9342 6.20291Z"
                  fill="#54708C"
                />
              </svg>
            </PremiumUnit>
          </UnitsForLabelAndValue>
        </PremiumInputContainer>
      </>
    );
  }

  //   date
  if (type === 'date') {
    const handleChangeValue = (name, value) => {
      let dateToSet = undefined;
      if (value) {
        dateToSet = moment(value).format('YYYY-MM-DD');
      }
      if (props.onChange !== undefined) {
        // we send the value to the parent component
        props.onChange(dateToSet, form);
      } else {
        form.setFieldValue(field.name, dateToSet);
      }
    };
    return (
      <Input
        type="date"
        className={className}
        hasError={hasError}
        isDisabled={isDisabled}
        isRequired={isRequired}
        label={label}
        value={value}
        width={props.width}
        units={units}
        disableClear={disableClear}
        {...cleanFormPropsForInput}
        {...cleanFieldPropsForInput}
        {...cleanPropsForInput}
        handleChangeValue={handleChangeValue}
      />
    );
  }

  //   time
  if (type === 'time') {
    const handleChangeValue = (name, value) => {
      if (props.onChange) {
        props.onChange(value, form);
      } else {
        form.setFieldValue(field.name, value);
      }
    };
    return (
      <Input
        type="time"
        className={className}
        hasError={hasError}
        isDisabled={isDisabled}
        isRequired={isRequired}
        label={label}
        value={value}
        width={props.width}
        units={units}
        {...cleanFormPropsForInput}
        {...cleanFieldPropsForInput}
        {...cleanPropsForInput}
        handleChangeValue={handleChangeValue}
      />
    );
  }

  //   search
  if (type === 'search') {
    return (
      <PremiumInputContainer
        width={props.width}
        isDisabled={isDisabled}
        hasError={hasError}
        className={className}
        hasFocus={hasFocus}
        ref={clickRefFrom}
        onClick={() => {
          if (isDisabled) return;
          setHasFocus(true);
          setInputFocus();
        }}
      >
        {!showInput ? (
          <>
            <PremiumLabelBig>
              {label}
              {isRequired && <RequiredIndicator />}
            </PremiumLabelBig>
            <Invisible>
              <input
                {...cleanFormPropsForInput}
                type={type}
                id={id}
                value={value}
                ref={inputRef}
                disabled={isDisabled}
                {...cleanFieldPropsForInput}
                autoFocus={props.autoFocus}
                onFocus={() => {
                  setHasFocus(true);
                  setInputFocus();
                }}
                onBlur={() => {
                  setHasFocus(false);
                }}
                readOnly={true}
              />
            </Invisible>
            <UnitsForLabelAndValue noPadding={true}>
              <PremiumUnit>
                <SearchIcon />
              </PremiumUnit>
            </UnitsForLabelAndValue>
          </>
        ) : (
          <>
            <LabelAndValue>
              <PremiumLabelSmall htmlFor={id}>
                {label}
                {isRequired && <RequiredIndicator />}
              </PremiumLabelSmall>
              <Value>
                <input
                  type={type}
                  id={id}
                  value={value}
                  ref={inputRef}
                  disabled={isDisabled}
                  {...cleanFormPropsForInput}
                  {...cleanFieldPropsForInput}
                  {...props}
                  onFocus={() => {
                    setHasFocus(true);
                    setInputFocus();
                  }}
                  onBlur={() => {
                    setHasFocus(false);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      if (props.onEnter !== undefined) {
                        props.onEnter();
                      }
                    }
                  }}
                />
              </Value>
            </LabelAndValue>

            <UnitsForLabelAndValue noPadding={true}>
              <PremiumUnit>
                <SearchIcon />
              </PremiumUnit>
            </UnitsForLabelAndValue>
          </>
        )}
      </PremiumInputContainer>
    );
  }

  //    NUMBER
  if (type === 'number') {
    const handleChangeValue = (name, value) => {
      if (props.onChange) {
        props.onChange(value, form);
      } else {
        form.setFieldValue(field.name, value);
      }
    };

    return (
      <Input
        type="number"
        className={className}
        handleChangeValue={handleChangeValue}
        hasError={hasError}
        isDisabled={isDisabled}
        isRequired={isRequired}
        label={label}
        value={value}
        width={props.width}
        units={units}
        min={min}
        {...cleanFormPropsForInput}
        {...cleanFieldPropsForInput}
        {...cleanPropsForInput}
      />
    );
  }

  //   select with search
  if (type === 'select-with-search') {
    return <SelectWithSearchInput {...props} />;
  }

  if (type === 'phone') {
    const phoneInputProps = {
      handleChangeValue: (name, value) => {
        form.setFieldValue(field.name, value);
      },
      id,
      hasError,
      label,
      name: field.name,
      value: field.value,
      isDisabled,
      width: props.width,
      lang: 'en',
      isRequired,
    };

    return <PhoneInput {...phoneInputProps} />;
  }

  //   TEXT or PASSWORD
  if (type === 'text' || type === 'password') {
    const setValue = (event, field) => {
      if (props.onChange !== undefined) {
        // we send the event to the parent component
        props.onChange(event, form);
      } else {
        form.setFieldValue(field.name, event.target.value);
      }
    };
    return (
      <PremiumInputContainer
        width={props.width}
        isDisabled={isDisabled}
        hasError={hasError}
        className={className}
        hasFocus={hasFocus}
        ref={clickRefFrom}
        onClick={() => {
          if (isDisabled) return;
          setHasFocus(true);
          setInputFocus();
        }}
      >
        {!showInput ? (
          <>
            <PremiumLabelBig>
              {label}
              {isRequired && <RequiredIndicator />}
            </PremiumLabelBig>
            <Invisible>
              <input
                {...cleanFormPropsForInput}
                type={type}
                id={id}
                value={value}
                ref={inputRef}
                disabled={isDisabled}
                {...cleanFieldPropsForInput}
                onFocus={() => {
                  setHasFocus(true);
                  setInputFocus();
                }}
                onBlur={() => {
                  setHasFocus(false);
                }}
                readOnly={true}
                onChange={(event) => {
                  setValue(event, field);
                }}
              />
            </Invisible>
            <PremiumUnit>{units} </PremiumUnit>
          </>
        ) : (
          <>
            <LabelAndValue>
              <PremiumLabelSmall htmlFor={id}>
                {label}
                {isRequired && <RequiredIndicator />}
              </PremiumLabelSmall>
              <Value>
                <input
                  type={type}
                  id={id}
                  value={value}
                  ref={inputRef}
                  disabled={isDisabled}
                  {...cleanFormPropsForInput}
                  {...cleanFieldPropsForInput}
                  {...cleanPropsForInput}
                  onFocus={() => {
                    setHasFocus(true);
                    setInputFocus();
                  }}
                  onBlur={() => {
                    setHasFocus(false);
                  }}
                  onChange={(event) => {
                    setValue(event, field);
                  }}
                />
              </Value>
            </LabelAndValue>
            {!!units && (
              <UnitsForLabelAndValue>
                <PremiumUnit>{units} </PremiumUnit>
              </UnitsForLabelAndValue>
            )}
          </>
        )}
      </PremiumInputContainer>
    );
  }
};

const PremiumInputWrapper = ({ ...props }) => {
  const { form, field, customErrorFieldName, showErrors, customHasError } = props;
  const id = field.name;
  const errorFieldName = customErrorFieldName ?? field.name;
  const errorMsg = _.get(form.errors, errorFieldName);
  const hasError = !!customHasError || (!!errorMsg && !!showErrors);
  const value = field.value;
  return <PremiumInput id={id} value={value} hasError={hasError} {...props} />;
};

export const FormikPremiumInput = ({ ...props }) => {
  return (
    <Field
      component={PremiumInputWrapper}
      {...props}
      style={{
        display: 'flex',
      }}
    />
  );
};
